import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Alert,
  Backdrop,
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AngleRightIcon from 'common/customIcons/angle_right';
import DeleteIcon from 'common/customIcons/delete';
import PlusIcon from 'common/customIcons/plus';
import MDButton from 'components/MDButton';
import CommonQuillEditor from 'common/editor';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDistributors } from '../../redux/slices/distributor';
import {
  createBrand,
  deleteBrand,
  getBrandDetails,
  updateBrand,
  uploadBrandImage,
} from '../../redux/slices/hyperwolfBrand';
import { openSnackbar } from '../../redux/slices/common';
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from '../../redux/slices/modalSlice';
import { validateHyperwolfBrand } from '../../validations/brand';
import { convertAndExtracHWBrandtMeta } from 'utilities/common';
import { buildBrandMetaPrompt, buildBannerAltPrompt } from 'prompt';

const BASE_PATH = '/hyperwolf/brands';

const initialBrandInfo = {
  brandName: '',
  brandHeading: '',
  brandSlug: '',
  salesRepresentativeName: '',
  salesRepresentativePhone: '',
  salesRepresentativeEmail: '',
  websiteUrl: '',
  billingEmail: '',
  shortDescription: '',
  bottomDescription: '',
};

const STILO_GENERATE_ENDPOINT = '/api/v1/generate/description';
const STILO_API_BASE_URL = process.env.REACT_APP_STILO_API_BASE_URL || '';
const STILO_API_KEY = process.env.REACT_APP_API_AUTH_KEY || '';

function limitToWordCount(value, maxWords = 10) {
  const words = String(value ?? '').trim().split(/\s+/).filter(Boolean);
  return words.slice(0, maxWords).join(' ');
}

function getVendorIdentifier(vendor) {
  if (!vendor) return '';
  if (typeof vendor === 'string') return vendor;
  return vendor.vendorId || vendor.blazeVendorId || vendor._id || '';
}

export default function AddBrandForm() {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id') || ''; // _id – used for GET details
  const brandId = searchParams.get('brandId') || ''; // brandId – used for PUT update
  const navigate = useNavigate();
  const isEditMode = !!(id || brandId);

  const [status, setStatus] = useState('Active');
  const [logoPreview, setLogoPreview] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [bannerRemoved, setBannerRemoved] = useState(false);
  const [brandInfo, setBrandInfo] = useState(initialBrandInfo);
  const [faqSectionTitle, setFaqSectionTitle] = useState("Faq's");
  const [faqItems, setFaqItems] = useState([{ question: '', answer: '' }]);
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [isPromptLoading, setPromptIsLoading] = useState(false);
  const [bannerAltText, setBannerAltText] = useState('');
  const [isBannerAltManual, setIsBannerAltManual] = useState(false);
  const [isBannerAltLoading, setIsBannerAltLoading] = useState(false);
  const [vendorList, setVendorList] = useState([]);
  const [socialLinks, setSocialLinks] = useState({
    facebookUrl: '',
    instagramUrl: '',
    twitterUrl: '',
    linkedInUrl: '',
    youtubeUrl: '',
  });
  const [errors, setErrors] = useState({});

  const dispatch = useDispatch();
  const { distributorsList = [], isLoading: distributorsLoading = false } =
    useSelector((state) => state.distributor);
  const {
    isLoading = false,
    isUploading = false,
    isFetchingDetails = false,
    brandDetails = null,
  } = useSelector((state) => state.hyperwolfBrand);

  const pageLoading =
    distributorsLoading || isLoading || isUploading || isFetchingDetails;

  const canGenerateMeta =
    !isPromptLoading &&
    Boolean(STILO_API_BASE_URL) &&
    Boolean(STILO_API_KEY) &&
    Boolean((brandInfo.brandName || '').trim());

  useEffect(() => {
    // Fetch only active distributors for vendor list
    dispatch(fetchDistributors({ skip: 0, limit: 500, status: 'Active' }));
  }, [dispatch]);

  useEffect(() => {
    const detailsId = id || brandId;
    if (detailsId) {
      dispatch(getBrandDetails(detailsId));
    }
  }, [id, brandId, dispatch]);

  useEffect(() => {
    const editId = id || brandId;
    if (!editId || !brandDetails) return;
    const d = brandDetails;
    setBrandInfo({
      brandName: d.brandName ?? '',
      brandHeading: d.heading ?? '',
      brandSlug: d.slug ?? d.brandSlug ?? '',
      salesRepresentativeName: d.salesRepresentativeName ?? '',
      salesRepresentativePhone: d.salesRepresentativePhone ?? '',
      salesRepresentativeEmail: d.salesRepresentativeEmail ?? '',
      websiteUrl: d.websiteUrl ?? '',
      billingEmail: d.billingEmail ?? '',
      shortDescription: d.shortDescription ?? '',
      bottomDescription: d.bottomDescription ?? '',
    });
    setStatus(d.status === true || d.status === 'active' ? 'Active' : 'Inactive');
    setLogoPreview(d.logo || null);
    setBannerPreview(d.brandImage ?? d.banner ?? null);
    setLogoRemoved(false);
    setBannerRemoved(false);
    const initialBannerAlt = d.bannerAltText ?? d.bannerAlt ?? d.altText ?? '';
    setBannerAltText(initialBannerAlt);
    setIsBannerAltManual(Boolean(String(initialBannerAlt).trim()));
    setFaqSectionTitle(d.faqSectionTitle ?? "Faq's");
    setFaqItems(
      Array.isArray(d.faqs) && d.faqs.length > 0
        ? d.faqs.map((f) => ({
          question: f.question ?? '',
          answer: f.answer ?? '',
        }))
        : [{ question: '', answer: '' }]
    );
    setMetaTitle(d.metaTitle ?? '');
    setMetaDescription(d.metaDescription ?? '');
    setVendorList(
      Array.isArray(d.vendorList)
        ? d.vendorList.map((vendor) => getVendorIdentifier(vendor)).filter(Boolean)
        : []
    );
    setSocialLinks({
      facebookUrl: d.facebookUrl ?? '',
      instagramUrl: d.instagramUrl ?? '',
      twitterUrl: d.twitterUrl ?? '',
      linkedInUrl: d.linkedInUrl ?? '',
      youtubeUrl: d.youtubeUrl ?? '',
    });
  }, [id, brandId, brandDetails]);

  const generateBannerAltTextWithAi = async (fileName = '') => {
    if (!STILO_API_BASE_URL || !STILO_API_KEY) return;
    setIsBannerAltLoading(true);
    try {
      const prompt = buildBannerAltPrompt({
        brandName: brandInfo.brandName,
        heading: brandInfo.brandHeading,
        imageName: fileName,
      });

      const res = await fetch(`${STILO_API_BASE_URL}${STILO_GENERATE_ENDPOINT}`, {
        method: 'POST',
        body: JSON.stringify({ prompt }),
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': STILO_API_KEY,
        },
      });

      if (!res?.body) throw new Error('No response body for banner alt text');

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        fullContent += decoder.decode(value, { stream: true });
      }

      const cleaned = String(fullContent || '')
        .replace(/\*\*/g, '')
        .replace(/^(Meta Title|Meta Description)\s*:?.*$/gim, '')
        .replace(/^[\s"'`-]+|[\s"'`-]+$/g, '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)[0] || '';

      if (!isBannerAltManual) {
        const finalAltText = limitToWordCount(cleaned.slice(0, 125), 10);
        setBannerAltText(finalAltText);
      }
    } catch (err) {
      if (!isBannerAltManual) setBannerAltText('');
    } finally {
      setIsBannerAltLoading(false);
    }
  };

  const handleBrandInfoChange = (e) => {
    const { name, value } = e.target;
    if (name === 'salesRepresentativePhone') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setBrandInfo((prev) => ({ ...prev, [name]: digitsOnly }));
    } else {
      setBrandInfo((prev) => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoPreview(URL.createObjectURL(file));
      setLogoFile(file);
      setLogoRemoved(false);
    }
    e.target.value = '';
  };

  const handleBannerChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setBannerPreview(URL.createObjectURL(file));
      setBannerFile(file);
      setBannerRemoved(false);
      setBannerAltText('');
      setIsBannerAltManual(false);
      generateBannerAltTextWithAi(file?.name || '');
    }
    e.target.value = '';
  };

  const clearLogo = () => {
    setLogoPreview(null);
    setLogoFile(null);
    setLogoRemoved(true);
  };

  const clearBanner = () => {
    setBannerPreview(null);
    setBannerFile(null);
    setBannerRemoved(true);
    setBannerAltText('');
    setIsBannerAltManual(false);
  };

  const addFaq = () => {
    setFaqItems((prev) => [...prev, { question: '', answer: '' }]);
  };

  const removeFaq = (index) => {
    setFaqItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateFaq = (index, field, value) => {
    setFaqItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleSubmit = async () => {
    const validation = validateHyperwolfBrand(brandInfo);
    if (!validation.isValid) {
      setErrors(validation.errors);
      // Scroll to the first invalid field
      const firstKey = Object.keys(validation.errors || {})[0];
      if (firstKey) {
        setTimeout(() => {
          const el =
            document.querySelector(`[name="${firstKey}"]`) ||
            document.getElementById(firstKey);
          if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          if (el?.focus) el.focus();
        }, 0);
      }
      return;
    }
    setErrors({});

    let logoUrl = '';
    let bannerUrl = '';

    if (logoFile) {
      const body = new FormData();
      body.append('filePath', logoFile);
      try {
        const res = await dispatch(uploadBrandImage(body)).unwrap();
        logoUrl = res?.fileUrl ?? '';
      } catch {
        dispatch(
          openSnackbar({
            content: 'Logo upload failed',
            color: 'error',
            icon: 'warning',
          })
        );
        return;
      }
    }

    if (bannerFile) {
      const body = new FormData();
      body.append('filePath', bannerFile);
      try {

        const res = await dispatch(uploadBrandImage(body)).unwrap();
        bannerUrl = res?.fileUrl ?? '';
      } catch {
        dispatch(
          openSnackbar({
            content: 'Banner upload failed',
            color: 'error',
            icon: 'warning',
          })
        );
        return;
      }
    }

    const faqs = faqItems
      .filter((item) => (item.question || '').trim() || (item.answer || '').trim())
      .map((item) => ({
        question: item.question || '',
        answer: item.answer || '',
      }));

    const payload = {
      brandName: brandInfo.brandName || '',
      heading: brandInfo.brandHeading || '',
      slug: brandInfo.brandSlug || '',
      salesRepresentativeName: brandInfo.salesRepresentativeName || '',
      salesRepresentativePhone: brandInfo.salesRepresentativePhone || '',
      salesRepresentativeEmail: brandInfo.salesRepresentativeEmail || '',
      websiteUrl: brandInfo.websiteUrl || '',
      billingEmail: brandInfo.billingEmail || '',
      shortDescription: brandInfo.shortDescription || '',
      bottomDescription: brandInfo.bottomDescription || '',
      faqSectionTitle: faqSectionTitle || '',
      faqs,
      metaTitle: metaTitle || '',
      metaDescription: metaDescription || '',
      status: status === 'Active',
      vendorList: Array.isArray(vendorList) ? vendorList : [],
      facebookUrl: socialLinks.facebookUrl || '',
      instagramUrl: socialLinks.instagramUrl || '',
      twitterUrl: socialLinks.twitterUrl || '',
      linkedInUrl: socialLinks.linkedInUrl || '',
      youtubeUrl: socialLinks.youtubeUrl || '',
    };

    // Always include logo/banner keys:
    // - If explicitly removed (edit): send "".
    // - If new upload: send uploaded URL.
    // - Otherwise (edit): send existing value from details.
    // - Otherwise (create): send "".
    payload.logo = logoRemoved
      ? ''
      : (logoUrl || (isEditMode ? (brandDetails?.logo ?? '') : ''));
    payload.banner = bannerRemoved
      ? ''
      : (bannerUrl || (isEditMode ? (brandDetails?.brandImage ?? brandDetails?.banner ?? '') : ''));
    payload.bannerAltText = bannerAltText || '';

    const updateId = brandId || id;

    const submitAction = isEditMode
      ? dispatch(updateBrand({ brandId: updateId, data: payload }))
      : dispatch(createBrand(payload));

    submitAction
      .unwrap()
      .then((res) => {
        dispatch(
          openSnackbar({
            content: isEditMode
              ? 'Brand updated successfully'
              : 'Brand created successfully',
            color: 'success',
            icon: 'check',
          })
        );
        navigate(BASE_PATH);
      })
      .catch((err) => {
        const message =
          err?.message || err?.error ||
          (isEditMode ? 'Failed to update brand' : 'Failed to create brand');
        dispatch(
          openSnackbar({
            content: message,
            color: 'error',
            icon: 'warning',
          })
        );
      });
  };

  const handleGenerateMeta = async () => {
    if (!canGenerateMeta) return;
    setPromptIsLoading(true);
    try {
      setMetaTitle('');
      setMetaDescription('');
      setBrandInfo((prev) => ({ ...prev, shortDescription: '', bottomDescription: '' }));

      const prompt = buildBrandMetaPrompt({
        brandName: brandInfo.brandName,
        heading: brandInfo.brandHeading,
        shortDescription: brandInfo.shortDescription,
        bottomDescription: brandInfo.bottomDescription,
      });

      const res = await fetch(`${STILO_API_BASE_URL}${STILO_GENERATE_ENDPOINT}`, {
        method: 'POST',
        body: JSON.stringify({ prompt }),
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': STILO_API_KEY,
        },
      });

      if (!res?.body) throw new Error('No response body');

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        fullContent += chunk;
        const parsed = convertAndExtracHWBrandtMeta(fullContent);

        setMetaTitle(parsed?.metaTitle || '');
        setMetaDescription(parsed?.metaDescription || '');
        if (parsed?.shortDescription) setBrandInfo((prev) => ({ ...prev, shortDescription: parsed.shortDescription }));
        if (parsed?.longDescription) setBrandInfo((prev) => ({ ...prev, bottomDescription: parsed.longDescription }));
      }

      const parsed = convertAndExtracHWBrandtMeta(fullContent);
      setMetaTitle(parsed?.metaTitle || '');
      setMetaDescription(parsed?.metaDescription || '');
      if (parsed?.shortDescription) setBrandInfo((prev) => ({ ...prev, shortDescription: parsed.shortDescription }));
      if (parsed?.longDescription) setBrandInfo((prev) => ({ ...prev, bottomDescription: parsed.longDescription }));
    } catch (err) {
    } finally {
      setPromptIsLoading(false);
    }
  };


  return (
    <Box p={{ xs: 2, sm: 3, lg: 4 }} position="relative">
      <Backdrop
        open={pageLoading}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" alignItems="center" color="text.main" lineHeight={1} mb={2}>
        <IconButton onClick={() => navigate(-1)}>
          <AngleRightIcon fontSize="14px" />
        </IconButton>
        <Typography variant="h6" fontWeight="semibold" ml={0.75}>
          Brands
        </Typography>
      </Box>

      <Card sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2.5, boxShadow: 'none' }}>
        {/* Row: Add Brand title + Status */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Typography variant="h5" fontWeight="semibold" color="secondary">
            {isEditMode ? 'Edit Brand' : 'Add Brand'}
          </Typography>

          <Box display="flex" alignItems="center" gap={1.25}>
            <Typography variant="h6" fontWeight="semibold" color="secondary">
              Status
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              p={0.75}
              borderRadius={2}
              bgcolor="#F2F3F7"
            >
              <MDButton
                sx={{
                  minWidth: '92px',
                  borderRadius: 1.75,
                  minHeight: '28px',
                  p: 0.25,
                  fontWeight: 500,
                }}
                type="button"
                variant={status === 'Active' ? 'contained' : 'text'}
                color={status === 'Active' ? 'success' : 'dark'}
                size="small"
                onClick={() => setStatus('Active')}
              >
                Active
              </MDButton>
              <MDButton
                sx={{
                  minWidth: '92px',
                  borderRadius: 1.75,
                  minHeight: '28px',
                  p: 0.25,
                  fontWeight: 500,
                }}
                type="button"
                variant={status === 'Inactive' ? 'contained' : 'text'}
                color={status === 'Inactive' ? 'error' : 'dark'}
                size="small"
                onClick={() => setStatus('Inactive')}
              >
                Inactive
              </MDButton>
            </Stack>
          </Box>
        </Stack>

        {/* Brand Logo + Brand Banner - layout: [preview] [Change Image] side by side */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight="600" color="secondary" mb={1.5}>
              Brand Logo
            </Typography>
            {logoPreview ? (
              <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#fafafa',
                    width: 150,
                    height: 150,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src={logoPreview}
                    alt="Brand logo"
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                  <IconButton
                    size="small"
                    onClick={clearLogo}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: 'common.white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}
                    aria-label="Remove logo"
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
                <MDButton
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: 'grey.100',
                    borderColor: 'grey.300',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'grey.200', borderColor: 'grey.400' },
                  }}
                  component="label"
                >
                  Change Image
                  <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                </MDButton>
              </Stack>
            ) : (
              <MDButton
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 1.5,
                  bgcolor: 'grey.100',
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'grey.200', borderColor: 'grey.400' },
                }}
                component="label"
              >
                Upload Image
                <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
              </MDButton>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" fontWeight="600" color="secondary" mb={1.5}>
              Brand Banner
            </Typography>
            {bannerPreview ? (
              <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#fafafa',
                    width: 150,
                    height: 150,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src={bannerPreview}
                    alt={bannerAltText || 'Brand banner'}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />
                  <IconButton
                    size="small"
                    onClick={clearBanner}
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      bgcolor: 'rgba(0,0,0,0.55)',
                      color: 'common.white',
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                    }}
                    aria-label="Remove banner"
                  >
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Box>
                <MDButton
                  variant="outlined"
                  size="small"
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: 'grey.100',
                    borderColor: 'grey.300',
                    color: 'text.primary',
                    '&:hover': { bgcolor: 'grey.200', borderColor: 'grey.400' },
                  }}
                  component="label"
                >
                  Change Image
                  <input type="file" hidden accept="image/*" onChange={handleBannerChange} />
                </MDButton>
              </Stack>
            ) : (
              <MDButton
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: 1.5,
                  bgcolor: 'grey.100',
                  borderColor: 'grey.300',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'grey.200', borderColor: 'grey.400' },
                }}
                component="label"
              >
                Upload Image
                <input type="file" hidden accept="image/*" onChange={handleBannerChange} />
              </MDButton>
            )}
            {!!String(bannerAltText || '').trim() && (
              <TextField
                fullWidth
                label="Banner Image Alt Text"
                placeholder="Banner alt text"
                variant="outlined"
                size="small"
                value={bannerAltText}
                onChange={(e) => {
                  const limitedText = limitToWordCount(e.target.value, 10);
                  setBannerAltText(limitedText);
                  setIsBannerAltManual(true);
                }}
                // helperText="Max 10 words."
                sx={{ mt: 1.5 }}
              />
            )}
          </Grid>
        </Grid>
      </Card>

      {/* Brand Info + Short Description + Bottom Description */}
      <Card sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2.5, boxShadow: 'none' }}>
        <Typography variant="h5" fontWeight="semibold" color="secondary" mb={3}>
          Brand Info
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="brandName"
              value={brandInfo.brandName}
              onChange={handleBrandInfoChange}
              label="Brand Name"
              placeholder="Brand Name"
              variant="outlined"
              size="small"
              error={!!errors.brandName}
              helperText={errors.brandName}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="brandHeading"
              value={brandInfo.brandHeading}
              onChange={handleBrandInfoChange}
              label="Brand Heading (H1)"
              placeholder="Brand Heading (H1)"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="brandSlug"
              value={brandInfo.brandSlug}
              onChange={handleBrandInfoChange}
              label="Brand Slug"
              placeholder="Brand Slug"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="salesRepresentativeName"
              value={brandInfo.salesRepresentativeName}
              onChange={handleBrandInfoChange}
              label="Brand Sales Representative Name"
              placeholder="Brand Sales Representative Name"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="salesRepresentativePhone"
              value={brandInfo.salesRepresentativePhone}
              onChange={handleBrandInfoChange}
              label="Brand Sales Representative Phone Number"
              placeholder="Brand Sales Representative Phone Number"
              variant="outlined"
              size="small"
              error={!!errors.salesRepresentativePhone}
              helperText={errors.salesRepresentativePhone}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="salesRepresentativeEmail"
              value={brandInfo.salesRepresentativeEmail}
              onChange={handleBrandInfoChange}
              label="Brand Sales Representative Email"
              placeholder="Brand Sales Representative Email"
              variant="outlined"
              size="small"
              type="email"
              error={!!errors.salesRepresentativeEmail}
              helperText={errors.salesRepresentativeEmail}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="websiteUrl"
              value={brandInfo.websiteUrl}
              onChange={handleBrandInfoChange}
              label="Brand Website URL"
              placeholder="Brand Website URL"
              variant="outlined"
              size="small"
              error={!!errors.websiteUrl}
              helperText={errors.websiteUrl}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              name="billingEmail"
              value={brandInfo.billingEmail}
              onChange={handleBrandInfoChange}
              label="Brand Billing Email"
              placeholder="Brand Billing Email"
              variant="outlined"
              size="small"
              type="email"
              error={!!errors.billingEmail}
              helperText={errors.billingEmail}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              fullWidth
              options={Array.isArray(distributorsList) ? distributorsList : []}
              getOptionLabel={(option) =>
                option?.distributorName ?? option?.name ?? option?._id ?? ''
              }
              value={
                Array.isArray(distributorsList)
                  ? distributorsList.filter(
                    (d) => {
                      const vendorId = getVendorIdentifier(d);
                      return vendorId && vendorList.includes(vendorId);
                    }
                  )
                  : []
              }
              onChange={(_, selected) => {
                setVendorList(
                  selected?.map((d) => getVendorIdentifier(d)).filter(Boolean) ?? []
                );
              }}
              renderTags={() => null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Vendors"
                  placeholder="Select distributors"
                  variant="outlined"
                  size="small"
                />
              )}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  height: 37,
                  minHeight: 37,
                  paddingTop: 0,
                  paddingBottom: 0,
                  alignItems: 'center',
                  '& .MuiOutlinedInput-input': {
                    padding: '8.5px 14px',
                  },
                },
                '& .MuiAutocomplete-tag': {
                  margin: '0 2px 0 0',
                  height: 22,
                  maxHeight: 22,
                },
              }}
            />
            {Array.isArray(distributorsList) && vendorList.length > 0 && (
              <Box
                mt={1}
                display="flex"
                flexWrap="wrap"
                gap={0.75}
                sx={{
                  maxHeight: 108,
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                {vendorList.map((vendorId) => {
                  const matchedVendor = distributorsList.find(
                    (d) => getVendorIdentifier(d) === vendorId
                  );
                  const vendorLabel =
                    matchedVendor?.distributorName ||
                    matchedVendor?.vendorName ||
                    matchedVendor?.name ||
                    vendorId;
                  return (
                    <Chip
                      key={vendorId}
                      label={vendorLabel}
                      size="small"
                      onDelete={() =>
                        setVendorList((prev) => prev.filter((id) => id !== vendorId))
                      }
                    />
                  );
                })}
              </Box>
            )}
          </Grid>
        </Grid>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={2}
          mt={4}
          mb={3}
        >
          <Alert severity="info" sx={{ flex: 1 }}>
            The brand name is required to generate the meta title, description, alt text, short description, and bottom description.
          </Alert>
          <MDButton
            variant="contained"
            color="primary"
            onClick={handleGenerateMeta}
            disabled={!canGenerateMeta}
            startIcon={!isPromptLoading && <SmartToyIcon />}
            sx={{ minWidth: 160, alignSelf: { xs: 'flex-end', sm: 'center' } }}
          >
            {isPromptLoading ? <CircularProgress size={20} color="inherit" /> : 'Generate'}
          </MDButton>
        </Stack>

        <Typography variant="h6" fontWeight="600" color="secondary" mt={4} mb={1.5}>
          Short Description
        </Typography>
        <CommonQuillEditor
          value={brandInfo.shortDescription}
          onChange={(html) => setBrandInfo((prev) => ({ ...prev, shortDescription: html }))}
          height="200px"
          allowHeading={true}
        />

        <Typography variant="h6" fontWeight="600" color="secondary" mt={4} mb={1.5}>
          Bottom Description
        </Typography>
        <CommonQuillEditor
          value={brandInfo.bottomDescription}
          onChange={(html) => setBrandInfo((prev) => ({ ...prev, bottomDescription: html }))}
          height="200px"
          allowHeading={true}
        />
      </Card>

      {/* FAQs Section */}
      <Card sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2.5, boxShadow: 'none' }}>
        <Typography variant="h5" fontWeight="semibold" color="secondary" mb={3}>
          FAQs Section
        </Typography>
        <Typography variant="body2" fontWeight="600" color="secondary" mb={1}>
          Section Title (H2)
        </Typography>
        <TextField
          fullWidth
          value={faqSectionTitle}
          onChange={(e) => setFaqSectionTitle(e.target.value)}
          placeholder="Faq's"
          variant="outlined"
          size="small"
          sx={{ mb: 3 }}
        />

        {faqItems.map((faq, index) => (
          <Box
            key={index}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.300',
              bgcolor: 'grey.50',
              position: 'relative',
            }}
          >
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <MDButton
                variant="text"
                size="small"
                onClick={() => removeFaq(index)}
                // disabled={faqItems.length <= 1}
                sx={{
                  color: '#c62828',
                  minWidth: 'auto',
                  '&:hover': { bgcolor: 'rgba(198, 40, 40, 0.08)' },
                }}
              >
                <DeleteIcon width="1rem" height="1rem" style={{ marginRight: 4 }} />
                Delete
              </MDButton>
            </Box>
            <Typography variant="body2" fontWeight="600" color="secondary" mb={1}>
              Question
            </Typography>
            <CommonQuillEditor
              value={faq.question}
              onChange={(html) => updateFaq(index, 'question', html)}
              height="120px"
              allowHeading={true}
            />
            <Typography variant="body2" fontWeight="600" color="secondary" mt={2} mb={1}>
              Answer
            </Typography>
            <CommonQuillEditor
              value={faq.answer}
              onChange={(html) => updateFaq(index, 'answer', html)}
              height="120px"
              allowHeading={true}
            />
          </Box>
        ))}


        <Box display="flex" justifyContent="flex-start" mb={2}>
          <MDButton
            variant="outlined"
            color="info"
            size="medium"
            onClick={addFaq}
            startIcon={<PlusIcon width="18px" height="18px" />}
            sx={{
              bgcolor: 'white',
              borderColor: 'info.main',
              color: 'info.main',
              fontWeight: 700,
              borderRadius: 10,
              '&:hover': {
                bgcolor: 'grey.50',
                borderColor: 'info.dark',
                color: 'info.dark',
              },
            }}
          >
            Add FAQ
          </MDButton>
        </Box>

      </Card>

      {/* Meta Properties */}
      <Card sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2.5, boxShadow: 'none' }}>
        <Typography variant="h5" fontWeight="semibold" color="secondary" mb={2}>
          Meta Properties
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              value={metaTitle}
              onChange={(e) => setMetaTitle(e.target.value)}
              label="Meta Title"
              placeholder="Meta Title"
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              label="Description"
              placeholder="Description"
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
      </Card>

      {/* Social Links
      <Card sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 2.5, boxShadow: 'none' }}>
        <Typography variant="h5" fontWeight="semibold" color="secondary" mb={3}>
          Social Links
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              value={socialLinks.facebookUrl}
              onChange={(e) =>
                setSocialLinks((prev) => ({ ...prev, facebookUrl: e.target.value }))
              }
              label="Facebook URL"
              placeholder="https://facebook.com/..."
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              value={socialLinks.instagramUrl}
              onChange={(e) =>
                setSocialLinks((prev) => ({ ...prev, instagramUrl: e.target.value }))
              }
              label="Instagram URL"
              placeholder="https://instagram.com/..."
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              value={socialLinks.twitterUrl}
              onChange={(e) =>
                setSocialLinks((prev) => ({ ...prev, twitterUrl: e.target.value }))
              }
              label="Twitter / X URL"
              placeholder="https://twitter.com/..."
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              value={socialLinks.linkedInUrl}
              onChange={(e) =>
                setSocialLinks((prev) => ({ ...prev, linkedInUrl: e.target.value }))
              }
              label="LinkedIn URL"
              placeholder="https://linkedin.com/..."
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              value={socialLinks.youtubeUrl}
              onChange={(e) =>
                setSocialLinks((prev) => ({ ...prev, youtubeUrl: e.target.value }))
              }
              label="YouTube URL"
              placeholder="https://youtube.com/..."
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
      </Card> */}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={4} mb={2}>
        <MDButton
          sx={{ minWidth: '120px' }}
          type="button"
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={isLoading || isUploading}
        >
          Save
        </MDButton>
        {isEditMode && (
          <MDButton
            sx={{ minWidth: '120px', backgroundColor: '#fcd3d0 !important', color: '#FD4438' }}
            type="button"
            variant="contained"
            onClick={() =>
              dispatch(
                openCustomModalDialog({
                  modalData: {
                    deleteHandler: () => {
                      const idToDelete = brandId || brandDetails?.brandId;
                      if (!idToDelete) return;
                      dispatch(setModalLoader(true));
                      dispatch(deleteBrand(idToDelete))
                        .unwrap()
                        .then((res) => {
                          dispatch(closeCustomModalDialog());
                          dispatch(openSnackbar({
                            content: res?.message || 'Brand deleted successfully',
                            color: 'success',
                            icon: 'check',
                          }));
                          navigate(BASE_PATH);
                        })
                        .catch((err) => {
                          dispatch(setModalLoader(false));
                          dispatch(closeCustomModalDialog());
                          dispatch(openSnackbar({
                            content: err?.message || err?.error || 'Failed to delete brand',
                            color: 'error',
                            icon: 'warning',
                          }));
                        });
                    },
                    deleteId: brandId || brandDetails?.brandId || id,
                    deleteType: 'Brand',
                  },
                  modal: 'DELETE_MODAL',
                })
              )
            }
          >
            Delete
          </MDButton>
        )}
        <MDButton
          sx={{ minWidth: '120px' }}
          type="button"
          variant="outlined"
          color="dark"
          onClick={() => navigate(BASE_PATH)}
        >
          Cancel
        </MDButton>
      </Stack>
    </Box>
  );
}
